import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { formatCompactCount, type CmCategory, type SignalTag } from '@bloo-team/api-client'
import { caseLabel, useCmCases, useCmStatuses, useCmUsers } from '../lib/case-mgmt'
import { Icon, Pagination } from '@bloo-team/ui'
import { Briefcase, ChevronDown, ChevronRight, Flag, Network, Pencil, Star } from '@bloo-team/ui/icons'
import { PageStrip } from '@bloo-team/core-shell'
import { PickList } from '@bloo-team/ui'
import { SlideOver } from '@bloo-team/ui'
import { isDemoSession } from '@bloo-team/core-shell'
import {
  APPLY_SUGGESTIONS_NOTE,
  ApplySuggestionsDialog,
  CloseDialog,
  DEFAULT_SECTIONS,
  DeepPanel,
  EntityFlyout,
  EscalateDialog,
  HeatStrip,
  RemarkDialog,
  SignalPanel,
  StatsBand,
  StorylineGraph,
  StorylinePanel,
} from '../components/signal'
import { SignalTagChips } from '../components/signal/SignalTagChips'
import { resolveTags, tagFilterOptions, tagLookup, tagMatchKeys } from '../lib/signal-tags'
import { isSignalTab, type CloseDialogProps, type EscalateDialogProps, type SignalTab } from '../components/signal/types'
import { useAttackTactics } from '../lib/coverage'
import { localHuntStore } from '../lib/hunt-store'
import { usePostureStats } from '../lib/overview'
import { navPath } from '@bloo-team/core-shell'
import { useSession } from '@bloo-team/core-shell'
import { suggestTriage, type TriageSuggestion } from '../lib/signal-ai'
import { unsafeStreams, type FlatQuery } from '../lib/signal-addfilters'
import { dispositionsByDetection } from '../lib/signal-dispositions'
import { entityContextFor, entityHistory, matchingRules, prevalence, recentWithEntity, useSignalBaseline } from '../lib/signal-history'
import { openSignalAsHunt, relatedSignals } from '../lib/signal-hunt'
import {
  EMPTY_FILTER,
  entityKey,
  matchesFilter,
  readSignal,
  SEVERITY_ORDER,
  signalFirstSeen,
  type Disposition,
  type Signal,
  type SignalEntity,
  type SignalFilter,
  type SignalSeverity,
} from '../lib/signal-model'
import {
  assignStatusFor,
  isQueueTab,
  lockRefusal,
  matchesStage,
  QUEUE_TAB_LABEL,
  queueTabsFor,
  silentStreams,
  stageOptions,
  tacticCells,
  toggleTactic,
  type QueueTab,
} from '../lib/signal-queue'
import { useSignalSettings } from '../lib/signal-settings'
import { absTime } from '../lib/local-time'
import { isStoryTab, rankSubgraphs, SUBGRAPH_CAP, subgraphFor, watchedValues, type PromotedSubgraph, type StoryTab } from '../lib/signal-subgraphs'
import { useTriage } from '../lib/signal-store'
import {
  MEMBER_FIELD_LABEL,
  MEMBER_FIELDS,
  PAGE_SIZES,
  TABLE_COLUMN_LABEL,
  TABLE_COLUMNS,
  useSignalView,
  type MemberField,
  type PageSize,
  type TableColumn,
} from '../lib/signal-view'
import {
  useAddRemark,
  useCheckLockedWorkbook,
  useConnectedSignals,
  useEscalate,
  useFlagFalsePositive,
  useSignalDocument,
  useSignalEvents,
  useSignalEvidence,
  useSignalKb,
  useSignalCount,
  useSignalPage,
  useSignalTags,
  useSignals,
  useSignalTimeslice,
  useStorylineAttribution,
  useStreamDailyCounts,
  WINDOW_OPTIONS,
  type SignalWindow,
} from '../lib/signals'
import { useSaveSuppression, useSuppressionRules } from '../lib/suppression'
import { useWorkbookMeta } from '../lib/workbook-blocks'
import type { TimeBucket } from '@bloo-team/core-shell'

/**
 * Vantage · Signals — the triage queue, split-pane layout (docs/signals.md).
 *
 * Top to bottom: page strip (window) · stats band · queue tabs (Unassigned ·
 * All) · volume chart with the ATT&CK heat strip · filters
 * (incl. stage, stream health) · lens row (+ columns, density) · the split:
 * the list on the left, the docked signal pane on the right (⤢ promotes it
 * to the full sheet).
 *
 * Storylines are the backend's connected-signals subgraphs (decision S31;
 * docs/signals.md §Storylines), List or Graph; the queue tab and filter row
 * do not change them (S20's storyline clause superseded). Flat /
 * by-detection / by-tactic are secondary; Flat pages on the server
 * (signals/list addfilters), the others group the window rows. Every store
 * write, route change and mutation lives here — the signal components are
 * props-in/callbacks-out (components/signal/types.ts). The selected signal
 * and the queue tab are search params so a triage link is shareable;
 * filters are component state except the legacy `?wbId=` contract.
 */

const LENSES = ['storylines', 'flat', 'detection', 'tactic'] as const
type Lens = (typeof LENSES)[number]
const LENS_LABEL: Record<Lens, string> = {
  storylines: 'Storylines',
  flat: 'Flat',
  detection: 'By detection',
  tactic: 'By tactic',
}

export interface SignalsSearch {
  window?: SignalWindow
  lens?: Lens
  wbId?: string
  signal?: string
  /** Queue tab; absent = Unassigned. */
  queue?: QueueTab
  /** Storylines view; absent = list. */
  view?: 'graph'
  /** Focused storyline (Subgraph.key) in Graph mode. */
  sg?: string
  /** The storyline panel's tab; absent = signals. */
  stab?: StoryTab
  /** The docked signal pane's section to scroll to; absent = overview (the top). */
  ptab?: SignalTab
}

/** Bulk actions stop here (decision S14). */
export const BULK_CAP = 50

/** The window the page opens on when the URL names none. */
export const DEFAULT_WINDOW: SignalWindow = '1d'

const isWindow = (v: unknown): v is SignalWindow => typeof v === 'string' && (WINDOW_OPTIONS as readonly string[]).includes(v)
const isLens = (v: unknown): v is Lens => typeof v === 'string' && (LENSES as readonly string[]).includes(v)

/** Route-shim search validation — the composer's `validateSearch`. */
export function validateSignalsSearch(search: Record<string, unknown>): SignalsSearch {
  return {
    ...(isWindow(search.window) ? { window: search.window } : {}),
    ...(isLens(search.lens) ? { lens: search.lens } : {}),
    ...(typeof search.wbId === 'string' && search.wbId ? { wbId: search.wbId } : {}),
    ...(isQueueTab(search.queue) && search.queue !== 'unassigned' ? { queue: search.queue } : {}),
    ...(search.view === 'graph' ? { view: 'graph' as const } : {}),
    ...(typeof search.sg === 'string' && search.sg ? { sg: search.sg } : {}),
    // The Kill Chain tab is retired: its `?stab=chain` and the older canvas
    // toggle's `?chain=1` open the MITRE tab.
    ...(isStoryTab(search.stab) && search.stab !== 'signals'
      ? { stab: search.stab }
      : search.stab === 'chain' || search.chain === '1' || search.chain === 1
        ? { stab: 'mitre' as const }
        : {}),
    ...(isSignalTab(search.ptab) && search.ptab !== 'overview' ? { ptab: search.ptab } : {}),
    // `sig_id` is the LEGACY deep-link param — every stored $signal_url uses
    // it (cases-write-capture.md). Alias onto `signal`; the new name wins.
    ...(typeof search.signal === 'string' && search.signal
      ? { signal: search.signal }
      : typeof search.sig_id === 'string' && search.sig_id
        ? { signal: search.sig_id }
        : {}),
  }
}

/** The queue as Sources' state chips: each with its window count (… while it loads), the picked one outlined. */
function QueueChips({ tabs, queue, counts, onPick }: { tabs: readonly QueueTab[]; queue: QueueTab; counts: Record<QueueTab, number | undefined>; onPick: (q: QueueTab) => void }) {
  return (
    <span className="hud-row sg-queue" role="group" aria-label="Queue">
      {tabs.map((q) => (
        <button key={q} type="button" className="list-chip" aria-pressed={queue === q} onClick={() => onPick(q)}>
          {QUEUE_TAB_LABEL[q]} <span className="n">{counts[q] === undefined ? '…' : counts[q].toLocaleString('en-US')}</span>
        </button>
      ))}
    </span>
  )
}

/** A relative age with the absolute local time on hover. */
function Ago({ at }: { at: number | null }) {
  return (
    <span className="muted mono" title={at === null ? undefined : absTime(at)}>
      {age(at)}
    </span>
  )
}

/** Coarse relative age — same voice as the hunts table. */
function age(at: number | null): string {
  if (at === null) return '—'
  const m = Math.round((Date.now() - at) / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 48) return `${h}h`
  return `${Math.round(h / 24)}d`
}

/** A storyline's kept members, newest end first (module-level: hooks read it without a dependency). */
function membersOf(st: PromotedSubgraph): PromotedSubgraph['signals'] {
  return [...st.signals].sort((a, b) => (b.end ?? 0) - (a.end ?? 0))
}

/** Timeslice width per window (decision S13): hour / 6h / day. */
export function sliceMsFor(window: SignalWindow): number {
  return window === '1d' ? 3_600_000 : window === '7d' ? 6 * 3_600_000 : 86_400_000
}

/** Bucket width: the narrowest gap between served buckets (live timeslice_data is window / 30), else the window's slice. */
export function sliceOf(buckets: readonly TimeBucket[], window: SignalWindow): number {
  let w = Infinity
  for (let i = 1; i < buckets.length; i++) w = Math.min(w, buckets[i]!.t - buckets[i - 1]!.t)
  return Number.isFinite(w) && w > 0 ? w : sliceMsFor(window)
}

interface RowRef {
  key: string
  kind: 'story' | 'group' | 'signal'
  id: string
}

/* ------------------------------------------------------------ small parts */

/**
 * Dot + uppercase word, the one way severity is written in every lens. The
 * data-severity attribute stays on the dot: bloo-ui draws an edge stripe on
 * any non-tr [data-severity] element, which on a wrapper would show as a
 * stray 2px bar beside the dot.
 */
function SeverityTag({ severity }: { severity: SignalSeverity }) {
  return (
    <span className="sg-sev">
      <span className="sg-sev-dot" data-severity={severity} aria-hidden="true" />
      {severity}
    </span>
  )
}

function entitiesText(s: Signal): string {
  return s.entities.map((e) => `${e.field.toLowerCase()} ${e.value}`).join(' · ')
}

function MemberRow({
  signal,
  focused,
  selected,
  fields,
  tags,
  onOpen,
  rowRef,
  fold,
}: {
  signal: Signal
  focused: boolean
  selected: boolean
  fields: readonly MemberField[]
  /** The row's tags resolved against signals/tag/list. */
  tags: readonly SignalTag[]
  onOpen: () => void
  rowRef: (el: HTMLElement | null) => void
  /** A kept storyline member with merged siblings: the ×N toggle lists them under it. */
  fold?: { count: number; open: boolean; onToggle: () => void }
}) {
  const has = (f: MemberField) => fields.includes(f)
  const row = (
    <button
      type="button"
      ref={rowRef}
      className="sg-member"
      data-focused={focused || undefined}
      data-open={selected || undefined}
      tabIndex={-1}
      onClick={onOpen}
    >
      <SeverityTag severity={signal.severity} />
      <span className="sg-member-name">{signal.detectionName || '—'}</span>
      {has('time') ? <Ago at={signal.at} /> : null}
      {has('tags') ? <SignalTagChips tags={tags} fit /> : null}
      {has('entities') ? <span className="sg-member-entities muted">{entitiesText(signal)}</span> : null}
    </button>
  )
  if (!fold) return row
  // The ×N toggle sits beside the row, not in it: a button can't hold a button.
  return (
    <div className="sg-member-folds">
      {row}
      <button type="button" className="sg-badge" aria-expanded={fold.open} aria-label={`${fold.count} merged signals`} onClick={fold.onToggle}>
        ×{fold.count}
      </button>
    </div>
  )
}

/** Entity box → server page: one request per typing pause, not per keystroke. */
const ENTITY_DEBOUNCE_MS = 300

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

/** Column labels → ids, for the PickList (it shows strings). */
const COLUMN_BY_LABEL = new Map(TABLE_COLUMNS.map((c) => [TABLE_COLUMN_LABEL[c], c]))
const FIELD_BY_LABEL = new Map(MEMBER_FIELDS.map((f) => [MEMBER_FIELD_LABEL[f], f]))

/* --------------------------------------------------------------- page */

export interface SignalsPageProps {
  clusterId: string
  scopeId: string
  search: SignalsSearch
}

export function SignalsPage({ clusterId, scopeId, search }: SignalsPageProps) {
  const navigate = useNavigate()
  const session = useSession()
  const demo = isDemoSession()
  const scope = useMemo(() => ({ clusterId, scopeId }), [clusterId, scopeId])
  const user = session?.user.email || session?.user.name || 'analyst'

  const window: SignalWindow = search.window ?? DEFAULT_WINDOW
  const lens: Lens = search.lens ?? 'storylines'
  // A stale tab (the retired Mine) reads as the default; so does False positives on Storylines, which does not offer it.
  const queue: QueueTab = isQueueTab(search.queue) && queueTabsFor(lens).includes(search.queue) ? search.queue : 'unassigned'
  const wbId = search.wbId ?? null
  const selectedUuid = search.signal ?? null
  /** URL `view` — named storyView: `view` is already the per-viewer columns/density store (useSignalView). */
  const storyView: 'list' | 'graph' = search.view === 'graph' ? 'graph' : 'list'
  const sgParam = search.sg ?? null
  const storyTab: StoryTab = isStoryTab(search.stab) ? search.stab : 'signals'

  const setSearch = useCallback(
    (patch: Partial<Record<keyof SignalsSearch, string | number | undefined>>) => {
      const merged: Record<string, string | number> = {}
      const cur: Record<string, string | number | undefined> = { ...search, ...patch }
      for (const [k, v] of Object.entries(cur)) if (v) merged[k] = v
      void navigate({
        to: '/$clusterId/$scopeId/vantage/signals',
        params: { clusterId, scopeId },
        search: merged as SignalsSearch,
        replace: true,
      })
    },
    [navigate, clusterId, scopeId, search],
  )

  /**
   * Only By detection / By tactic group the window rows (≤ 2000 per tab).
   * Storylines and Flat do without them: options from attack/matrix,
   * cases/stats and workbook/meta, the tab total from a one-row count call,
   * signals by uuid from the 30d baseline.
   */
  const windowLens = lens === 'detection' || lens === 'tactic'
  const signalsQ = useSignals(scope, window, { assignStatus: assignStatusFor(queue), enabled: windowLens, fpOnly: queue === 'fp' })
  /** The queue counts for the window (unfiltered): the queue pills, the header, the SIGNALS ring. */
  const unassignedQ = useSignalCount(scope, window, assignStatusFor('unassigned'))
  const allQ = useSignalCount(scope, window, assignStatusFor('all'))
  // Only where the chip is: Storylines does not offer False positives.
  const fpQ = useSignalCount(scope, window, assignStatusFor('fp'), { fpOnly: true, enabled: queueTabsFor(lens).includes('fp') })
  const queueCount: Record<QueueTab, number | undefined> = { unassigned: unassignedQ.data, all: allQ.data, fp: fpQ.data }
  /** The 30d baseline for entity context — the same query the 30d window uses. */
  const baselineQ = useSignalBaseline(scope)
  const postureQ = usePostureStats(scope, window)
  const tacticsQ = useAttackTactics(scope)
  const stageMetaQ = useWorkbookMeta({ clusterId, type: 'stage' })
  const dailyQ = useStreamDailyCounts(scope)
  const lockCheck = useCheckLockedWorkbook(scope)
  const { view, store: viewStore } = useSignalView()
  const { triage, store } = useTriage(clusterId, scopeId)
  const { settings, store: settingsStore } = useSignalSettings(clusterId, scopeId)
  const connected = useConnectedSignals(scope, window, { enabled: lens === 'storylines' })
  const flagFp = useFlagFalsePositive(scope)
  const addRemark = useAddRemark(scope)
  /** Volume chart: the server's per-bucket counts for the window (whole scope — no tab or filter). */
  const timesliceQ = useSignalTimeslice(scope, window)
  const saveSuppression = useSaveSuppression(scope)
  const escalate = useEscalate(scope)

  const [pickedFilter, setFilter] = useState<SignalFilter>(EMPTY_FILTER)
  /** The filter row's picks plus the queue's false-positive rule: hidden, or (False positives) the only ones. */
  const filter = useMemo<SignalFilter>(() => ({ ...pickedFilter, hideFp: queue !== 'fp', fpOnly: queue === 'fp' }), [pickedFilter, queue])
  const [stages, setStages] = useState<string[]>([])
  const [bucket, setBucket] = useState<{ start: number; end: number } | null>(null)
  const [wbDismissed, setWbDismissed] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  /** Kept storyline members whose merged (folded) uuids are listed under them. */
  const [foldOpen, setFoldOpen] = useState<ReadonlySet<string>>(new Set())
  const [storyKey, setStoryKey] = useState<string | null>(null)
  /** Graph mode, nothing focused: the storyline j/k last moved to — the pane shows it without focusing the canvas. */
  const [storyPick, setStoryPick] = useState<string | null>(null)
  // -1 = keyboard cursor not engaged: no row wears the focus outline until
  // the first j/k or click. Starting at 0 outlined the first row on load,
  // which read as a state the reader never asked for.
  const [focusRaw, setFocus] = useState(-1)
  const [nowMs] = useState(() => Date.now())
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set())
  /** Flat: the server orders by time; severity re-sorts the rows of the current page only. */
  const [sortKey, setSortKey] = useState<'time' | 'severity'>('time')
  const [pageNo, setPageNo] = useState(1)
  const [remarkReq, setRemarkReq] = useState<Signal | null>(null)
  const [deepOpen, setDeepOpen] = useState(false)
  /** The docked pane promoted to the full sheet (⤢). */
  const [sheetOpen, setSheetOpen] = useState(false)
  const [cheatsheet, setCheatsheet] = useState(false)
  const [closeReq, setCloseReq] = useState<{ signals: Signal[]; preset: Disposition | null } | null>(null)
  const [escalateReq, setEscalateReq] = useState<{
    signals: Signal[]
    /** A whole storyline: every merged uuid (some may not be loaded), its name for the case ↔ storyline map, and the dialog prefill. */
    story?: { uuids: string[]; graphName: string; prefill: EscalateDialogProps['prefill'] }
  } | null>(null)
  const [applyReq, setApplyReq] = useState<{ signal: Signal; suggestion: TriageSuggestion }[] | null>(null)
  const [pending, setPending] = useState<{ caseId: string; caseNumber: string | null; error: string } | null>(null)
  const [entityOpen, setEntityOpen] = useState<SignalEntity | null>(null)
  const [wbRefusal, setWbRefusal] = useState<{ uuid: string; message: string } | null>(null)
  /** ✕ on the pane: stop showing the first row by default until the next pick. */
  const [paneDismissed, setPaneDismissed] = useState(false)
  const entityInputRef = useRef<HTMLInputElement>(null)
  const rowEls = useRef(new Map<string, HTMLElement>())

  const wbActive = wbId !== null && wbDismissed !== wbId
  // A disabled query still hands back its cache; off the grouped lenses there are no window rows.
  const all = useMemo(() => (windowLens ? signalsQ.signals : []), [windowLens, signalsQ.signals])
  /* Escalate's Existing case picker: one V2 page, status / category filters. */
  const [pickPage, setPickPage] = useState(1)
  const [pickStatus, setPickStatus] = useState<string | null>(null)
  const [pickCategory, setPickCategory] = useState<CmCategory | null>(null)
  const pickQ = useCmCases(scope, {
    page: pickPage,
    pageSize: 25,
    ...(pickStatus ? { status: [pickStatus] } : {}),
    ...(pickCategory ? { category: [pickCategory] } : {}),
  }, escalateReq !== null)
  const cmStatusesQ = useCmStatuses(scope)
  const cmUsersQ = useCmUsers(scope, escalateReq !== null)
  /** Titles for the entity flyout's case links (the first V2 page). */
  const recentCasesQ = useCmCases(scope, { page: 1, pageSize: 200 }, entityOpen !== null)

  // Window + tab base (legacy wbId contract applied first).
  const base = useMemo(() => all.filter((s) => !wbActive || s.detectionId === wbId), [all, wbActive, wbId])

  /* ---------------------------------------------- Flat: one server page */

  /** The entity box shows every keystroke; the server page asks for the trimmed text once typing pauses. */
  const entityQuery = useDebounced(filter.entity.trim(), ENTITY_DEBOUNCE_MS)
  const flatQuery = useMemo<FlatQuery>(
    // The Detection picker is hidden on Flat (S47), so a pick made on a grouped lens does not narrow it.
    () => ({ filter: { ...filter, entity: entityQuery, detections: [] }, stages, wbId: wbActive ? wbId : null, range: bucket }),
    [filter, entityQuery, stages, wbActive, wbId, bucket],
  )
  /** Picked streams the Flat list leaves out of its server filter (CB-67). */
  const unsentStreams = useMemo(() => (lens === 'flat' ? unsafeStreams(filter.streams) : []), [lens, filter.streams])
  // Any change to what the page asks for starts again at page 1 with nothing ticked (adjusted during render, not in an effect).
  const flatKey = JSON.stringify([flatQuery, queue, window, view.pageSize])
  const [pageFor, setPageFor] = useState(flatKey)
  if (pageFor !== flatKey) {
    setPageFor(flatKey)
    setPageNo(1)
    setChecked(new Set())
  }
  const pageQ = useSignalPage(
    scope,
    window,
    { assignStatus: assignStatusFor(queue), flat: flatQuery, pageNo, pageSize: view.pageSize },
    { enabled: lens === 'flat' },
  )
  // A mutation that empties the last page (Mark FP, Add to case on Unassigned) steps back to the new last page.
  const lastPage = Math.max(1, Math.ceil(pageQ.count / view.pageSize))
  if (pageQ.data && !pageQ.isPlaceholderData && pageNo > lastPage) {
    setPageNo(lastPage)
    setFocus(-1)
  }
  const pageRows = useMemo(() => (lens === 'flat' ? pageQ.signals : []), [lens, pageQ.signals])

  const tabUuids = useMemo(() => new Set([...base, ...pageRows].map((s) => s.uuid)), [base, pageRows])


  /** Streams with no events so far today (daily-stream-details); muted before 06:00. Off the grouped lenses, the names are cases/stats'. */
  const silent = useMemo(() => {
    const names = new Set<string>(windowLens ? [] : (postureQ.data?.streamNames ?? []))
    for (const s of base) s.streams.forEach((x) => names.add(x))
    return silentStreams(dailyQ.data, [...names], nowMs)
  }, [dailyQ.data, base, nowMs, windowLens, postureQ.data])
  const silentSet = useMemo(() => new Set(silent), [silent])

  const options = useMemo(() => {
    const tactics = new Set<string>()
    const detections = new Set<string>()
    const streams = new Set<string>(silent)
    for (const s of base) {
      if (s.tactic) tactics.add(s.tactic)
      if (s.detectionName) detections.add(s.detectionName)
      s.streams.forEach((x) => streams.add(x))
    }
    const sorted = (set: Set<string>) => [...set].sort((a, b) => a.localeCompare(b))
    if (!windowLens) {
      // Storylines / Flat: tactics as attack/matrix spells them, streams from cases/stats, stages from workbook/meta (+ none); no detections.
      for (const x of postureQ.data?.streamNames ?? []) streams.add(x)
      return { tactics: [...(tacticsQ.data ?? [])], detections: [], streams: sorted(streams), stages: stageOptions(stageMetaQ.data, []) }
    }
    return {
      tactics: sorted(tactics),
      detections: sorted(detections),
      streams: sorted(streams),
      stages: stageOptions(stageMetaQ.data, base),
    }
  }, [base, silent, stageMetaQ.data, windowLens, postureQ.data, tacticsQ.data])

  /* ------------------------------------------- signal tags (tag/list) */

  // The Detections lookup: a raw row tag resolves by id, then by name; unknown or disabled tags never show.
  const signalTagsQ = useSignalTags(scope)
  const lookupTag = useMemo(() => tagLookup(signalTagsQ.data), [signalTagsQ.data])
  const tagsOf = useCallback((s: Signal) => resolveTags(s.tags, lookupTag), [lookupTag])
  /** The Tag filter: enabled tags by name; the filter itself holds ids. */
  const tagOptions = useMemo(() => tagFilterOptions(signalTagsQ.data), [signalTagsQ.data])
  const tagLabelById = useMemo(() => new Map(tagOptions.map((o) => [o.id, o.label])), [tagOptions])
  const tagIdByLabel = useMemo(() => new Map(tagOptions.map((o) => [o.label, o.id])), [tagOptions])

  /** Every filter except the time bucket — the grouped lenses, client-side over the window rows. */
  const matchesAll = useCallback(
    (s: Signal) =>
      // Client-side, a row tag matches a picked id or its name (demo rows carry names).
      matchesFilter(s, { ...filter, tags: tagMatchKeys(filter.tags, signalTagsQ.data) }, triage.get(s.uuid) ?? null) &&
      matchesStage(stages, s),
    [filter, stages, triage, signalTagsQ.data],
  )
  const filtered = useMemo(
    () => base.filter((s) => matchesAll(s) && (!bucket || (s.at >= bucket.start && s.at < bucket.end))),
    [base, matchesAll, bucket],
  )

  const byUuid = useMemo(() => new Map([...all, ...pageRows].map((s) => [s.uuid, s])), [all, pageRows])
  /** The 30d baseline by uuid — the open signal stays readable after it leaves the tab (e.g. escalated out of Unassigned). */
  const baselineByUuid = useMemo(() => new Map(baselineQ.signals.map((s) => [s.uuid, s])), [baselineQ.signals])
  const watched = useMemo(() => new Set(settings.watchlist), [settings.watchlist])
  /** Promotion (S34: total_weight / tactics) + watched (value match) + sort, over the job's first 50. */
  const ranked = useMemo(
    () => rankSubgraphs(connected.subgraphs, settings.promotion, watchedValues(settings.watchlist)),
    [connected.subgraphs, settings.promotion, settings.watchlist],
  )
  const storylines = ranked

  /** `?sg` when it names a shown storyline; an unknown or filtered-out key behaves like "all" (§6.4). */
  const focusKey = sgParam && storylines.some((s) => s.key === sgParam) ? sgParam : null
  /** A storyline member: window rows, then the 30d baseline (§6.6; the pane adds signals/data). */
  const resolveSignal = useCallback(
    (u: string): Signal | null => byUuid.get(u) ?? baselineByUuid.get(u) ?? null,
    [byUuid, baselineByUuid],
  )
  const buckets = useMemo(() => timesliceQ.data ?? [], [timesliceQ.data])
  /** Heat strip: `cases/stats` top_tactics for the window (top 10, whole scope); every other tactic reads 0. */
  const heat = useMemo(() => tacticCells(postureQ.data?.topTactics, tacticsQ.data), [postureQ.data, tacticsQ.data])

  /* -------------------------------------------------- AI (demo only) */

  const baseline = baselineQ.signals
  /** The 30d roll-up the suggestion reads. Live mode: none — there is no endpoint (BACKEND-ASKS). */
  const dispositions = useMemo(
    () => (demo ? dispositionsByDetection(baseline.length ? baseline : all, triage) : null),
    [demo, baseline, all, triage],
  )

  /** The current page, newest first as served; severity re-sorts only this page (the server has no severity order). */
  const sortedFlat = useMemo(
    () =>
      sortKey === 'time'
        ? pageRows
        : [...pageRows].sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) || b.at - a.at),
    [pageRows, sortKey],
  )
  /** The header checkbox: the first BULK_CAP rows as shown, and how many of them are ticked. */
  const pageVisible = useMemo(() => sortedFlat.slice(0, BULK_CAP), [sortedFlat])
  const pageTicked = pageVisible.filter((s) => checked.has(s.uuid)).length
  const pageTick: 'none' | 'some' | 'all' = pageTicked === 0 ? 'none' : pageTicked === pageVisible.length ? 'all' : 'some'

  const groups = useMemo(() => {
    if (lens !== 'detection' && lens !== 'tactic') return []
    const m = new Map<string, Signal[]>()
    for (const s of filtered) {
      const k = (lens === 'detection' ? s.detectionName : s.tactic) || '—'
      const g = m.get(k)
      if (g) g.push(s)
      else m.set(k, [s])
    }
    return [...m.entries()]
      .map(([key, members]) => ({ key, members: members.sort((a, b) => b.at - a.at) }))
      .sort((a, b) => b.members.length - a.members.length)
  }, [filtered, lens])

  /** The grouped lenses page client-side with the Flat pager: storylines (List view) and the By detection / By tactic groups. */
  // The page is held with the query it belongs to: any change to what is listed reads as page 1.
  const groupKey = JSON.stringify([lens, storyView, filter, stages, queue, window, bucket, view.pageSize])
  const [groupPaging, setGroupPaging] = useState({ key: groupKey, page: 1 })
  const groupPageNo = groupPaging.key === groupKey ? groupPaging.page : 1
  const groupTotal = lens === 'storylines' ? storylines.length : groups.length
  const groupLast = Math.max(1, Math.ceil(groupTotal / view.pageSize))
  const groupPage = Math.min(groupPageNo, groupLast)
  const pagedStorylines = useMemo(
    () => (storyView === 'list' ? storylines.slice((groupPage - 1) * view.pageSize, groupPage * view.pageSize) : storylines),
    [storylines, storyView, groupPage, view.pageSize],
  )
  const pagedGroups = useMemo(() => groups.slice((groupPage - 1) * view.pageSize, groupPage * view.pageSize), [groups, groupPage, view.pageSize])

  /** The keyboard's row model — what j/k walk, in on-screen order. Graph mode: the storylines (one force-laid canvas), none once one is focused. */
  const rows = useMemo<RowRef[]>(() => {
    const out: RowRef[] = []
    if (lens === 'storylines') {
      if (storyView === 'graph') {
        if (!focusKey) for (const st of storylines) out.push({ key: `story:${st.key}`, kind: 'story', id: st.key })
      } else {
        for (const st of pagedStorylines) {
          out.push({ key: `story:${st.key}`, kind: 'story', id: st.key })
          if (!expanded.has(st.key)) continue
          for (const m of membersOf(st)) {
            out.push({ key: `story:${st.key}:${m.uuid}`, kind: 'signal', id: m.uuid })
            if (foldOpen.has(m.uuid)) for (const f of m.folded) out.push({ key: `story:${st.key}:${f}`, kind: 'signal', id: f })
          }
        }
      }
    } else if (lens === 'flat') {
      for (const s of sortedFlat) out.push({ key: `flat:${s.uuid}`, kind: 'signal', id: s.uuid })
    } else {
      for (const g of pagedGroups) {
        out.push({ key: `group:${g.key}`, kind: 'group', id: g.key })
        if (expanded.has(g.key)) for (const s of g.members) out.push({ key: `group:${g.key}:${s.uuid}`, kind: 'signal', id: s.uuid })
      }
    }
    return out
  }, [lens, storyView, focusKey, storylines, pagedStorylines, sortedFlat, pagedGroups, expanded, foldOpen])

  /** The member a storyline opens: its newest open kept member (falls back to the newest). */
  const storyLead = useCallback(
    (st: PromotedSubgraph) => {
      const ms = membersOf(st)
      return (ms.find((m) => (triage.get(m.uuid)?.status ?? 'open') !== 'closed') ?? ms[0])?.uuid
    },
    [triage],
  )

  /** The signal the pane shows when the URL names none (not the storylines lens — that docks a storyline): the first row. */
  const autoLead = useMemo((): string | null => {
    if (lens === 'storylines') return null
    return (lens === 'flat' ? sortedFlat[0]?.uuid : groups[0]?.members[0]?.uuid) ?? null
  }, [lens, sortedFlat, groups])
  /** URL first; else the first row, so the pane is never empty on arrival (until ✕). */
  const openUuid = selectedUuid ?? (paneDismissed ? null : autoLead)
  const openStoryKey = selectedUuid ? storyKey : null
  /**
   * The storyline the pane shows (storylines lens, no signal open): `?sg`,
   * else (Graph, unfocused) the one j/k reached, else the first one on
   * arrival — until ✕.
   */
  const paneStory = useMemo((): PromotedSubgraph | null => {
    if (lens !== 'storylines' || selectedUuid) return null
    const find = (k: string | null) => (k ? (storylines.find((s) => s.key === k) ?? null) : null)
    return find(sgParam) ?? (storyView === 'graph' ? find(storyPick) : null) ?? (paneDismissed ? null : (storylines[0] ?? null))
  }, [lens, selectedUuid, storylines, sgParam, storyView, storyPick, paneDismissed])
  const attributionQ = useStorylineAttribution(scope, paneStory)

  // Clamped at read time — the row model shrinks under filters and lenses.
  // A disengaged cursor (-1) stays disengaged; rows[-1] is undefined, which
  // every consumer already treats as "no row".
  const focus = focusRaw < 0 ? -1 : Math.min(focusRaw, rows.length - 1)

  useEffect(() => {
    const row = rows[focus]
    if (row) rowEls.current.get(row.key)?.scrollIntoView?.({ block: 'nearest' })
  }, [focus, rows])

  /** §6.6: window rows → 30d baseline → signals/data (fetched only when both miss). */
  const localSelected = openUuid ? resolveSignal(openUuid) : null
  const docQ = useSignalDocument(scope, openUuid && !localSelected ? openUuid : null)
  const docSignal = useMemo(() => (docQ.data ? readSignal(docQ.data) : null), [docQ.data])
  const selected = localSelected ?? docSignal
  /** Not in the loaded rows of the current tab (Mine's local half included) — the pane header says so (§6.6). */
  // Only the grouped lenses know the tab's rows; elsewhere the label would be noise.
  const outsideTab = windowLens && selected !== null && !tabUuids.has(selected.uuid)

  /*
   * The docked pane's sections: each read runs only while its section is
   * expanded. The expanded set carries over from signal to signal; `?ptab`
   * (the section to scroll to) is always expanded — collapsing it drops `?ptab`.
   */
  const paneTab: SignalTab = isSignalTab(search.ptab) ? search.ptab : 'overview'
  const [paneSections, setPaneSections] = useState<ReadonlySet<SignalTab>>(() => new Set([...DEFAULT_SECTIONS, paneTab]))
  const togglePaneSection = (t: SignalTab) => {
    if (paneSections.has(t) && t === search.ptab) setSearch({ ptab: undefined })
    setPaneSections((s) => {
      const next = new Set(s)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })
  }
  const paneDocked = selected !== null && !sheetOpen
  const kbQ = useSignalKb(scope, selected, paneDocked && paneSections.has('kb'))
  const evidenceQ = useSignalEvidence(scope, selected, paneDocked && (paneSections.has('overview') || paneSections.has('events')))
  /** Raw events page per signal — a new signal starts on page 1. */
  const [eventsPage, setEventsPage] = useState<{ uuid: string; page: number } | null>(null)
  const eventsPageNo = selected && eventsPage?.uuid === selected.uuid ? eventsPage.page : 1
  const eventsQ = useSignalEvents(scope, paneDocked && paneSections.has('events') ? (evidenceQ.data?.taskId ?? null) : null, eventsPageNo)
  const errorText = (e: unknown) => (e instanceof Error ? e.message : 'unknown error')
  const rules = useSuppressionRules(scope)
  /** Rules whose every term matches one of the selected signal's entities. */
  const matchingSuppressions = useMemo(
    () => (selected && rules.data ? matchingRules(selected.entities, rules.data) : []),
    [selected, rules.data],
  )
  /** Q17: the storyline it was opened from, else the cached one holding it, else null (same-entity fallback). */
  const selectedStory = useMemo(
    () =>
      (openStoryKey ? ranked.find((s) => s.key === openStoryKey) : undefined) ??
      (selected ? subgraphFor(selected.uuid, ranked) : null),
    [ranked, openStoryKey, selected],
  )
  /** The panel's suggestion reads the real same-entity count for its evidence line. */
  const selectedSuggestion = useMemo(
    () =>
      selected && dispositions
        ? suggestTriage({ signal: selected, dispositions, subgraphs: ranked, related: relatedSignals(selected, baseline.length ? baseline : all).length })
        : null,
    [selected, dispositions, ranked, baseline, all],
  )

  /* ----------------------------------------------------- entity context */

  const entityContext = useMemo(
    () => (selected && baseline.length ? entityContextFor(selected.entities, baseline, nowMs) : undefined),
    [selected, baseline, nowMs],
  )
  const entityView = useMemo(() => {
    if (!entityOpen) return null
    const key = entityKey(entityOpen)
    const recent = recentWithEntity(key, baseline, 8)
    const caseIds = new Set<string>()
    for (const s of baseline) {
      if (!s.entities.some((e) => entityKey(e) === key)) continue
      const c = triage.get(s.uuid)?.caseId
      if (c) caseIds.add(c)
    }
    const known = new Map((recentCasesQ.data?.items ?? []).map((c) => [c.id, c]))
    return {
      history: entityHistory(key, baseline, nowMs),
      prevalence: prevalence(entityOpen.field, entityOpen.value, baseline),
      recent,
      // The recent-cases read names them; one beyond it shows — and says so.
      cases: [...caseIds].map((id) => {
        const c = known.get(id)
        return { id, number: caseLabel(c ?? { caseNumber: null }), title: c?.title ?? 'Not among the latest 200 cases' }
      }),
      suppressions: rules.data ? matchingRules([entityOpen], rules.data) : [],
      watched: watched.has(key),
    }
  }, [entityOpen, baseline, triage, recentCasesQ.data, rules.data, watched, nowMs])

  /* ----------------------------------------------------------- actions */

  const openSignal = useCallback(
    (uuid: string, story: string | null) => {
      setStoryKey(story)
      setDeepOpen(false)
      setWbRefusal(null)
      setPaneDismissed(false)
      setSearch({ signal: uuid })
    },
    [setSearch],
  )
  const closePanel = useCallback(() => {
    setDeepOpen(false)
    setSheetOpen(false)
    setPaneDismissed(true)
    setSearch({ signal: undefined })
  }, [setSearch])
  /** The pane shows a storyline: List writes `?sg`; Graph without a focus only remembers it (`?sg` would focus the canvas). */
  const selectStory = useCallback(
    (st: PromotedSubgraph) => {
      setPaneDismissed(false)
      setDeepOpen(false)
      if (storyView === 'graph' && !focusKey) {
        setStoryPick(st.key)
        setSearch({ signal: undefined })
      } else setSearch({ sg: st.key, signal: undefined })
    },
    [storyView, focusKey, setSearch],
  )
  const closeStory = useCallback(() => {
    setPaneDismissed(true)
    setStoryPick(null)
    setSearch({ sg: undefined, signal: undefined })
  }, [setSearch])
  /** Add to case for a whole storyline: every merged uuid, plus its name as `graph_name` (the server's storyline ↔ case key). Opens on New case. */
  const escalateStory = useCallback(
    (st: PromotedSubgraph) => {
      const loaded = st.uuids.map((u) => resolveSignal(u)).filter((x): x is Signal => x !== null)
      const description = [
        `${st.counts.signals} signals · ${st.counts.suspects} suspects · ${st.counts.targets} targets · score ${st.score}`,
        st.tactics.length ? `Tactics: ${st.tactics.join(', ')}` : '',
        st.entities.length ? `Entities: ${st.entities.map((e) => `${e.field.toLowerCase()} ${e.value}`).join(', ')}` : '',
      ]
        .filter(Boolean)
        .join('\n')
      setEscalateReq({
        signals: loaded,
        story: {
          uuids: st.uuids,
          graphName: st.title,
          prefill: { title: st.title, description, severity: st.severity, count: st.uuids.length },
        },
      })
    },
    [resolveSignal],
  )

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  /** The pane follows the keyboard cursor: a signal row opens itself, a storyline its panel. */
  const followRow = useCallback(
    (row: RowRef | undefined) => {
      if (!row) return
      if (row.kind === 'signal') openSignal(row.id, lens === 'storylines' ? (row.key.split(':')[1] ?? null) : null)
      else if (row.kind === 'story') {
        const st = storylines.find((x) => x.key === row.id)
        if (st) selectStory(st)
      }
    },
    [openSignal, selectStory, lens, storylines],
  )

  const signalsOf = (uuids: string[]) => uuids.map((u) => byUuid.get(u)).filter((s): s is Signal => !!s)

  const requestClose = (signals: Signal[], preset: Disposition | null = null) => {
    if (signals.length) setCloseReq({ signals, preset })
  }

  const onCloseConfirm: CloseDialogProps['onConfirm'] = (input) => {
    store.close(input.uuids, { disposition: input.disposition, dispositionType: input.dispositionType, note: input.note }, user)
    if (input.flagFp) flagFp.mutate({ uuids: input.uuids, fp: true })
    if (input.suppression) {
      const sup = input.suppression
      saveSuppression.mutate(
        { wbId: sup.wbId, wbName: sup.wbName, terms: sup.terms, enabled: true },
        { onSuccess: () => input.uuids.forEach((u) => store.log(u, `suppression rule created for ${sup.wbName || sup.wbId}`, user)) },
      )
    }
    setCloseReq(null)
    setChecked(new Set())
  }

  /** Bulk apply: one close per disposition group; nothing reaches the backend (no FP flag without its opt-in). */
  const onApplyConfirm = (groupsIn: { disposition: Disposition; uuids: string[] }[]) => {
    for (const g of groupsIn) store.close(g.uuids, { disposition: g.disposition, dispositionType: null, note: APPLY_SUGGESTIONS_NOTE }, user)
    setApplyReq(null)
    setChecked(new Set())
  }

  /**
   * Mark / unmark false positive on the server (legacy Tag False Positive); a flagged signal leaves Unassigned.
   * A failure shows on the page (`writeFailure`); `onDone` (the bulk bar's clear) runs only once the server said yes.
   */
  const toggleFp = (signals: Signal[], fp: boolean, onDone?: () => void) => {
    if (signals.length) flagFp.mutate({ uuids: signals.map((s) => s.uuid), fp }, onDone ? { onSuccess: onDone } : undefined)
  }
  /** A server write the page fired and nobody else reports: FP flag (row, bulk, pane, Close…) and the Close dialog's suppression rule. */
  const writeFailure = flagFp.isError
    ? { title: 'False positive flag failed', message: flagFp.error.message, dismiss: () => flagFp.reset() }
    : saveSuppression.isError
      ? { title: 'Suppression rule not saved', message: saveSuppression.error.message, dismiss: () => saveSuppression.reset() }
      : null

  const onRemarkConfirm = (remark: string) => {
    if (!remarkReq) return
    const uuid = remarkReq.uuid
    addRemark.mutate(
      { uuid, remark },
      {
        onSuccess: () => {
          store.log(uuid, 'remark added', user)
          setRemarkReq(null)
        },
      },
    )
  }

  const onEscalateConfirm: EscalateDialogProps['onConfirm'] = (input) => {
    if (!escalateReq) return
    const uuids = escalateReq.story?.uuids ?? escalateReq.signals.map((s) => s.uuid)
    if (!uuids.length) return
    escalate.mutate(
      { ...input, caseId: input.caseId ?? pending?.caseId, caseNumber: input.caseNumber ?? pending?.caseNumber, uuids },
      {
        onSuccess: (r) => {
          if (r.attached) {
            store.linkCase(uuids, r.caseId, user, r.caseNumber ?? '—')
            setPending(null)
            setEscalateReq(null)
            setChecked(new Set())
          } else {
            setPending({ caseId: r.caseId, caseNumber: r.caseNumber, error: r.error ?? 'attach failed' })
          }
        },
        onError: (e) => setPending((p) => (p ? { ...p, error: e.message } : p)),
      },
    )
  }

  const openAsHunt = useCallback(
    (signal: Signal) => {
      const huntId = openSignalAsHunt(localHuntStore(clusterId, scopeId), signal, triage.get(signal.uuid) ?? null, user)
      store.linkHunt(signal.uuid, huntId, user)
      void navigate({ to: navPath(clusterId, scopeId, `/vantage/hunts/${huntId}`) as string })
    },
    [clusterId, scopeId, triage, user, store, navigate],
  )

  const openSearch = (query: string) =>
    void navigate({ to: '/$clusterId/$scopeId/fabric/search', params: { clusterId, scopeId }, search: { q: query } })
  const openDetection = (signal: Signal) =>
    void navigate({
      to: '/$clusterId/$scopeId/vantage/detections',
      params: { clusterId, scopeId },
      search: signal.detectionId ? { sel: signal.detectionId } : { q: signal.detectionName },
    })
  /** Open workbook: the lock check first (workbook/checklockedworkbook); a refusal stays on the page. */
  const openWorkbook = (signal: Signal) => {
    const wb = signal.detectionId
    if (!wb) return
    setWbRefusal(null)
    lockCheck.mutate(
      { wbid: wb },
      {
        onSuccess: (check) => {
          const refusal = lockRefusal(check)
          if (refusal) setWbRefusal({ uuid: signal.uuid, message: refusal })
          else
            void navigate({
              to: '/$clusterId/$scopeId/vantage/detections/workbook',
              params: { clusterId, scopeId },
              search: { wb },
            })
        },
        onError: (e) => setWbRefusal({ uuid: signal.uuid, message: e.message }),
      },
    )
  }
  /** The signal a single-signal key acts on: the open panel, else the focused row. */
  const targetSignal = useCallback((): Signal | null => {
    if (selected) return selected
    const row = rows[focus]
    if (!row) return null
    if (row.kind === 'signal') return resolveSignal(row.id)
    if (row.kind === 'story') {
      const st = storylines.find((x) => x.key === row.id)
      const first = st ? storyLead(st) : undefined
      return first ? resolveSignal(first) : null
    }
    return null
  }, [selected, rows, focus, resolveSignal, storylines, storyLead])

  /** Accept (`a`): opens Close prefilled with the suggestion — never applies it. */
  const acceptSuggestion = useCallback(
    (signal: Signal) => {
      if (!dispositions) return
      const s = suggestTriage({ signal, dispositions, subgraphs: ranked, related: 0 })
      if (s.disposition === 'undetermined') return
      setCloseReq({ signals: [signal], preset: s.disposition })
    },
    [dispositions, ranked],
  )

  /* ---------------------------------------------------------- keyboard */

  const dialogOpen = closeReq !== null || escalateReq !== null || applyReq !== null || remarkReq !== null || cheatsheet || entityOpen !== null
  // The control the POINTER focused (a clicked button keeps focus). Taken from the
  // event order — pointerdown, focusin, click — not :focus-visible: Chromium flips a
  // clicked button to :focus-visible on the next key (the `j` before the Enter),
  // and jsdom never matches it.
  const pointerFocused = useRef<Element | null>(null)
  useEffect(() => {
    let pointer = false
    const onDown = () => {
      pointer = true
    }
    const onSettle = () => {
      pointer = false
    }
    const onFocusIn = (e: FocusEvent) => {
      pointerFocused.current = pointer && e.target instanceof Element ? e.target : null
      pointer = false
    }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('click', onSettle, true)
    document.addEventListener('keydown', onSettle, true)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('click', onSettle, true)
      document.removeEventListener('keydown', onSettle, true)
      document.removeEventListener('focusin', onFocusIn)
    }
  }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // A widget already handled it (graph node / Focus chip, sheet, picker).
      if (e.defaultPrevented) return
      const t = e.target as HTMLElement | null
      const tag = t?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return
      // Enter / Esc on a focused control belong to that control, not to the row cursor —
      // except Esc inside the storylines graph: its nodes are Tab + Enter, and Esc
      // (once the graph has cleared its own highlight) leaves the focus (§6.8).
      // Enter yields only to a KEYBOARD-focused control: a button that merely kept
      // focus after a click (Graph, then j) must not swallow the cursor's Enter.
      const control = t?.closest?.('button, a, [role="button"], input, select, textarea, summary')
      if (e.key === 'Enter' && control && pointerFocused.current !== t) return
      if (e.key === 'Escape' && control && !t?.closest?.('.sg-graph')) return
      if (e.key === '?') {
        e.preventDefault()
        setCheatsheet((v) => !v)
        return
      }
      if (dialogOpen) return
      switch (e.key) {
        case 'j':
        case 'k': {
          e.preventDefault()
          const next = e.key === 'j' ? Math.min(rows.length - 1, focus + 1) : Math.max(0, focus - 1)
          setFocus(next)
          followRow(rows[next])
          return
        }
        case 'Enter': {
          const row = rows[focus]
          if (!row) return
          e.preventDefault()
          if (row.kind === 'story') {
            const st = storylines.find((x) => x.key === row.id)
            if (st && storyView === 'graph') setSearch({ sg: st.key, signal: undefined })
            else if (st) toggleExpanded(st.key)
          } else if (row.kind === 'group') toggleExpanded(row.id)
          else openSignal(row.id, lens === 'storylines' ? (row.key.split(':')[1] ?? null) : null)
          return
        }
        case 'v':
          if (lens !== 'storylines') return
          e.preventDefault()
          setFocus(-1)
          setSearch({ view: storyView === 'graph' ? undefined : 'graph', sg: undefined })
          return
        case 'Escape':
          // The graph clears its own entity highlight first (it stops the event); then Esc leaves the focus.
          if (lens === 'storylines' && storyView === 'graph' && focusKey) {
            e.preventDefault()
            setSearch({ sg: undefined })
          }
          return
        case 'e':
          if (selected) setDeepOpen((v) => !v)
          return
        case 'h': {
          const tg = targetSignal()
          if (tg) openAsHunt(tg)
          return
        }
        case 'x': {
          const tg = targetSignal()
          if (tg) requestClose([tg])
          return
        }
        case 's': {
          const tg = targetSignal()
          if (tg) requestClose([tg], 'false_positive_logic')
          return
        }
        case 'a': {
          const tg = targetSignal()
          if (tg) acceptSuggestion(tg)
          return
        }
        case 'p':
        case '/':
          e.preventDefault()
          entityInputRef.current?.focus()
          return
        default:
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [dialogOpen, rows, focus, storylines, openSignal, followRow, lens, storyView, focusKey, setSearch, selected, targetSignal, openAsHunt, acceptSuggestion])

  /* --------------------------------------------------------------- bulk */

  const toggleChecked = (uuid: string) =>
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else if (next.size < BULK_CAP) next.add(uuid)
      return next
    })
  const checkedSignals = signalsOf([...checked])

  const quotaWarning = store.quotaWarning()
  const rowRefFor = (key: string) => (el: HTMLElement | null) => {
    if (el) rowEls.current.set(key, el)
    else rowEls.current.delete(key)
  }
  const focusedKey = rows[focus]?.key
  const columns = view.columns
  const has = (c: TableColumn) => columns.includes(c)

  /* ------------------------------------------------------------- render */

  const memberList = (uuids: string[], keyPrefix: string, story: string | null) => (
    <div className="sg-members">
      {signalsOf(uuids).map((s) => (
        <MemberRow
          key={s.uuid}
          signal={s}
          focused={focusedKey === `${keyPrefix}:${s.uuid}`}
          selected={openUuid === s.uuid}
          fields={view.memberFields}
          tags={tagsOf(s)}
          onOpen={() => openSignal(s.uuid, story)}
          rowRef={rowRefFor(`${keyPrefix}:${s.uuid}`)}
        />
      ))}
    </div>
  )

  /** Storyline members (§6.2): kept signals newest first, a ×N fold under each; a uuid no pool holds is a ghost row. */
  const storyMembers = (st: PromotedSubgraph, keyPrefix: string) => (
    <div className="sg-members">
      {membersOf(st).flatMap((m) => {
        const one = (uuid: string, fold?: { count: number; open: boolean; onToggle: () => void }) => {
          const s = resolveSignal(uuid)
          const k = `${keyPrefix}:${uuid}`
          return s ? (
            <MemberRow
              key={uuid}
              signal={s}
              focused={focusedKey === k}
              selected={openUuid === uuid}
              fields={view.memberFields}
              tags={tagsOf(s)}
              onOpen={() => openSignal(uuid, st.key)}
              rowRef={rowRefFor(k)}
              fold={fold}
            />
          ) : (
            <button
              key={uuid}
              type="button"
              ref={rowRefFor(k)}
              className="sg-member sg-member-ghost"
              data-focused={focusedKey === k || undefined}
              data-open={openUuid === uuid || undefined}
              tabIndex={-1}
              onClick={() => openSignal(uuid, st.key)}
            >
              <span className="sg-sev-dot" data-severity={m.severity} aria-hidden="true" />
              <span className="sg-member-name">{m.detectionName || '—'}</span>
              {fold ? <span className="sg-badge">×{fold.count}</span> : null}
              <Ago at={m.end} />
            </button>
          )
        }
        const open = foldOpen.has(m.uuid)
        const fold =
          m.count > 1
            ? {
                count: m.count,
                open,
                onToggle: () =>
                  setFoldOpen((p) => {
                    const n = new Set(p)
                    if (n.has(m.uuid)) n.delete(m.uuid)
                    else n.add(m.uuid)
                    return n
                  }),
              }
            : undefined
        return [
          one(m.uuid, fold),
          ...(open
            ? m.folded.map((f) => (
                <div key={`fold:${f}`} className="sg-fold">
                  {one(f)}
                </div>
              ))
            : []),
        ]
      })}
    </div>
  )

  const cell = (c: TableColumn, s: Signal) => {
    switch (c) {
      case 'time':
        return <Ago at={s.at} />
      case 'tactic':
        return <span className="muted">{s.tactic || '—'}</span>
      case 'entities':
        return <span className="mono">{entitiesText(s) || '—'}</span>
      case 'streams':
        return <span className="mono">{s.streams.join(', ') || '—'}</span>
      case 'occurrences':
        return <span title={String(s.occurrences)}>{formatCompactCount(s.occurrences)}</span>
      case 'stage':
        return <span className="mono">{s.stage ?? '—'}</span>
      case 'confidence':
        return <span className="muted">{s.confidence ?? '—'}</span>
      case 'first': {
        const first = signalFirstSeen(s)
        return <Ago at={first} />
      }
      case 'tags': {
        const tags = tagsOf(s)
        return tags.length ? <SignalTagChips tags={tags} fit /> : <span className="muted">—</span>
      }
      case 'uuid':
        return <span className="mono muted">{s.uuid}</span>
    }
  }

  /** The empty list, nothing filtered: the queue's own words. */
  const emptyLine = `No ${queue === 'unassigned' ? 'unassigned signals' : queue === 'fp' ? 'false positives' : 'signals'} in the last ${window}.`
  /** Any server filter on (tab and window aside) — picks the Flat empty-state line. */
  const flatFiltered =
    bucket !== null ||
    stages.length > 0 ||
    filter.entity.trim() !== '' ||
    filter.severities.length + filter.tactics.length + filter.detections.length + filter.streams.length + filter.tags.length > 0

  const queueChips = (
    <QueueChips
      tabs={queueTabsFor(lens)}
      queue={queue}
      counts={queueCount}
      onPick={(q) => {
        setFocus(-1)
        setChecked(new Set())
        setSearch({ queue: q === 'unassigned' ? undefined : q })
      }}
    />
  )

  /** Server pager, above and below the table (bloo-ui Pagination; total = the server's count). The top one carries the queue chips, as on Sources. */
  const pager = (where: 'top' | 'bottom') => (
    <Pagination
      page={pageNo}
      pageSize={view.pageSize}
      total={pageQ.count}
      onPageChange={(n) => {
        setChecked(new Set())
        setFocus(-1)
        setPageNo(n)
      }}
      onPageSizeChange={(n) => viewStore.set({ pageSize: n as PageSize })}
      pageSizeOptions={[...PAGE_SIZES]}
      busy={pageQ.isFetching}
      label={`Signals pages (${where})`}
      unit="signals"
    >
      {where === 'top' ? queueChips : null}
    </Pagination>
  )

  /** The grouped lenses' client pager: storylines (List view) or groups. */
  const groupPager = (where: 'top' | 'bottom') => (
    <Pagination
      page={groupPage}
      pageSize={view.pageSize}
      total={groupTotal}
      onPageChange={(n) => {
        setFocus(-1)
        setGroupPaging({ key: groupKey, page: n })
      }}
      onPageSizeChange={(n) => viewStore.set({ pageSize: n as PageSize })}
      pageSizeOptions={[...PAGE_SIZES]}
      label={`${lens === 'storylines' ? 'Storylines' : 'Groups'} pages (${where})`}
      unit={lens === 'storylines' ? 'storylines' : 'groups'}
    >
      {where === 'top' ? queueChips : null}
    </Pagination>
  )

  /** What heads the list: the pager with the queue chips when there are rows to page, else the chips alone. */
  const flatHasRows = lens === 'flat' && !pageQ.isLoading && !pageQ.isError && pageQ.count > 0
  const groupHasRows =
    lens === 'storylines'
      ? storyView === 'list' && !connected.isLoading && !connected.isError && storylines.length > 0
      : windowLens && !signalsQ.isLoading && !signalsQ.isError && groups.length > 0
  const listTop = flatHasRows ? pager('top') : groupHasRows ? groupPager('top') : <div className="hud-row sg-filters sg-list-top">{queueChips}</div>
  /** The header's page position: Flat's server page, or the grouped pager's. */
  const pageStat = flatHasRows ? `${pageNo}/${lastPage}` : groupHasRows ? `${groupPage}/${groupLast}` : null

  /** Row actions (hover / focus): false positive, add to case, remark. Their cell stops the click, so they never open the row. */
  const rowActions = (s: Signal) => (
    <span className="sg-row-actions">
      <button
        type="button"
        className="bui-btn bui-btn-ghost bui-btn-sm"
        aria-pressed={s.fp}
        aria-label={s.fp ? `Unmark false positive: ${s.detectionName}` : `Mark false positive: ${s.detectionName}`}
        title={s.fp ? 'Unmark false positive' : 'Mark false positive'}
        disabled={flagFp.isPending}
        onClick={() => toggleFp([s], !s.fp)}
      >
        <Icon icon={Flag} size={13} />
      </button>
      <button
        type="button"
        className="bui-btn bui-btn-ghost bui-btn-sm"
        aria-label={`Add to case: ${s.detectionName}`}
        title="Add to case"
        onClick={() => setEscalateReq({ signals: [s] })}
      >
        <Icon icon={Briefcase} size={13} />
      </button>
      <button
        type="button"
        className="bui-btn bui-btn-ghost bui-btn-sm"
        aria-label={`Add remark: ${s.detectionName}`}
        title={s.remark ? `Remark: ${s.remark}` : 'Add remark'}
        onClick={() => {
          addRemark.reset()
          setRemarkReq(s)
        }}
      >
        <Icon icon={Pencil} size={13} />
      </button>
    </span>
  )

  /** The storyline a member was opened from (storylines lens) — the signal pane links back to it. */
  const backStory = lens === 'storylines' && openStoryKey ? (storylines.find((x) => x.key === openStoryKey) ?? null) : null

  const storyPane = paneStory ? (
    <StorylinePanel
      key={paneStory.key}
      storyline={paneStory}
      tab={storyTab}
      onTab={(t) => setSearch({ stab: t === 'signals' ? undefined : t })}
      resolveSignal={resolveSignal}
      openUuid={openUuid}
      campaigns={attributionQ.data}
      campaignsLoading={attributionQ.isLoading}
      campaignsError={attributionQ.isError ? (attributionQ.error instanceof Error ? attributionQ.error.message : 'unknown error') : null}
      onRetryCampaigns={() => void attributionQ.refetch()}
      tacticOrder={tacticsQ.data ?? []}
      watched={watched}
      onOpenSignal={(u) => openSignal(u, paneStory.key)}
      onOpenEntity={setEntityOpen}
      onOpenSearch={openSearch}
      onToggleWatch={(k) => settingsStore.toggleWatch(k)}
      onAddToCase={() => escalateStory(paneStory)}
      onClose={closeStory}
    />
  ) : null

  const pane = selected ? (
    <SignalPanel
      variant={sheetOpen ? 'sheet' : 'docked'}
      signal={selected}
      triage={triage.get(selected.uuid) ?? null}
      // Related / same-entity context: the window rows on the grouped lenses, else the 30d baseline.
      all={windowLens ? all : baseline}
      storyline={selectedStory}
      outsideTab={outsideTab}
      backToStoryline={backStory ? { title: backStory.title, onBack: () => selectStory(backStory) } : undefined}
      tags={tagsOf(selected)}
      triageMap={triage}
      deepOpen={deepOpen}
      suppressions={matchingSuppressions}
      baseline={baseline.length ? baseline : undefined}
      entityContext={entityContext}
      suggestion={selectedSuggestion}
      onAcceptSuggestion={() => acceptSuggestion(selected)}
      onSuggestionNotHelpful={() => store.log(selected.uuid, 'AI suggestion marked not helpful', user)}
      onOpenWorkbook={() => openWorkbook(selected)}
      workbookBusy={lockCheck.isPending}
      workbookRefusal={wbRefusal?.uuid === selected.uuid ? wbRefusal.message : null}
      onPromote={() => setSheetOpen(true)}
      tab={paneTab}
      onTab={(t) => {
        setPaneSections((s) => (s.has(t) ? s : new Set([...s, t])))
        setSearch({ ptab: t === 'overview' ? undefined : t })
      }}
      expanded={paneSections}
      onToggleSection={togglePaneSection}
      kb={{ text: kbQ.data ?? '', loading: kbQ.isLoading, error: kbQ.isError ? errorText(kbQ.error) : null }}
      evidence={{
        loading: evidenceQ.isLoading,
        error: evidenceQ.isError ? errorText(evidenceQ.error) : null,
        noQuery: evidenceQ.data?.noQuery ?? false,
        facets: evidenceQ.data?.facets ?? [],
        onRetry: () => void evidenceQ.refetch(),
      }}
      events={{
        loading: evidenceQ.isLoading || eventsQ.isFetching,
        error: evidenceQ.isError ? errorText(evidenceQ.error) : eventsQ.isError ? errorText(eventsQ.error) : (eventsQ.data?.message ?? null),
        rows: eventsQ.data?.rows ?? [],
        fields: eventsQ.data?.fields ?? [],
        total: eventsQ.data?.total ?? null,
        pageNo: eventsPageNo,
        pageSize: 20,
        onPage: (page) => setEventsPage({ uuid: selected.uuid, page }),
      }}
      onToggleFalsePositive={() => toggleFp([selected], !selected.fp)}
      falsePositiveBusy={flagFp.isPending}
      onOpenEntity={setEntityOpen}
      deep={
        deepOpen ? (
          <DeepPanel
            clusterId={clusterId}
            scopeId={scopeId}
            signal={selected}
            triage={triage.get(selected.uuid) ?? null}
            related={
              selectedStory
                ? selectedStory.uuids
                    .filter((u) => u !== selected.uuid)
                    .map(resolveSignal)
                    .filter((s): s is Signal => s !== null)
                : relatedSignals(selected, windowLens ? all : baseline)
            }
            user={user}
            onPin={(pin) => store.pin(selected.uuid, pin, user)}
            onUnpin={(id) => store.unpin(selected.uuid, id)}
            onAddNote={(text) => store.addNote(selected.uuid, text, user)}
            onOpenSearch={openSearch}
            onOpenAsHunt={() => openAsHunt(selected)}
            onSelectSignal={(u) => openSignal(u, openStoryKey)}
          />
        ) : null
      }
      onClose={sheetOpen ? () => setSheetOpen(false) : closePanel}
      onToggleDeep={() => setDeepOpen((v) => !v)}
      onOpenAsHunt={() => openAsHunt(selected)}
      onEscalate={() => setEscalateReq({ signals: [selected] })}
      onCloseSignal={() => requestClose([selected])}
      onSuppress={() => requestClose([selected], 'false_positive_logic')}
      onReopen={() => store.reopen(selected.uuid, user)}
      onAddNote={(text) => store.addNote(selected.uuid, text, user)}
      onUnpin={(id) => store.unpin(selected.uuid, id)}
      onOpenSearch={openSearch}
      onOpenDetection={() => openDetection(selected)}
      onSelectSignal={(u) => openSignal(u, openStoryKey)}
    />
  ) : null

  return (
    <div>
      <PageStrip
        title="Vantage · Signals"
        stats={[
          { label: 'signals', value: postureQ.data ? formatCompactCount(postureQ.data.signals.count) : postureQ.isError ? '—' : '…' },
          { label: QUEUE_TAB_LABEL[queue].toLowerCase(), value: queueCount[queue] === undefined ? '…' : formatCompactCount(queueCount[queue]!) },
          { label: 'cases', value: postureQ.data ? formatCompactCount(postureQ.data.cases.count) : postureQ.isError ? '—' : '…' },
          ...(pageStat ? [{ label: 'page', value: pageStat }] : []),
        ]}
        meta={`scope ${scopeId}`}
        end={
          <div className="sg-pills" role="group" aria-label="Window">
            {WINDOW_OPTIONS.map((w) => (
              <button
                key={w}
                type="button"
                className="list-chip"
                aria-pressed={window === w}
                onClick={() => {
                  setBucket(null)
                  setSearch({ window: w })
                }}
              >
                {w}
              </button>
            ))}
          </div>
        }
      />

      <div className="hud-grid">
        {quotaWarning ? (
          <div className="hud-span-12">
            <div className="bui-alert bui-alert-error" role="alert">
              <span className="bui-alert-mark">✕</span>
              <span>
                <span className="bui-alert-title">Signal triage storage</span>
                {quotaWarning}
              </span>
            </div>
          </div>
        ) : null}
        {writeFailure ? (
          <div className="hud-span-12">
            <div className="bui-alert bui-alert-error" role="alert">
              <span className="bui-alert-mark">✕</span>
              <span className="hud-row hud-row-between" style={{ width: '100%' }}>
                <span>
                  <span className="bui-alert-title">{writeFailure.title}</span>
                  {writeFailure.message}
                </span>
                <button type="button" className="bui-btn bui-btn-ghost bui-btn-sm" onClick={writeFailure.dismiss}>
                  Dismiss
                </button>
              </span>
            </div>
          </div>
        ) : null}
        {wbActive ? (
          <div className="hud-span-12">
            <div className="bui-alert" role="status">
              <span className="hud-row hud-row-between" style={{ width: '100%' }}>
                <span>
                  Showing signals from workbook <span className="mono">{wbId}</span>
                </span>
                <button
                  type="button"
                  className="bui-btn bui-btn-ghost bui-btn-sm"
                  onClick={() => {
                    setWbDismissed(wbId)
                    setSearch({ wbId: undefined })
                  }}
                >
                  Dismiss
                </button>
              </span>
            </div>
          </div>
        ) : null}

        <div className="hud-span-12">
          <StatsBand
            queue={{ unassigned: unassignedQ.data, all: allQ.data }}
            posture={postureQ.data}
            loading={postureQ.isLoading}
            failed={postureQ.isError}
            window={window}
            volume={{
              buckets,
              sliceMs: sliceOf(buckets, window),
              range: bucket,
              onSelectRange: (start, end) => setBucket({ start, end }),
              onClearRange: () => setBucket(null),
              loading: timesliceQ.isLoading,
              failed: timesliceQ.isError,
            }}
          />
        </div>

        <div className="hud-panel hud-span-12" style={{ padding: 'var(--pnl-pad)' }}>
          <div className="hud-stack sg-page">
            {/* ATT&CK heat */}
            <div className="sg-strip">
              <HeatStrip
                cells={heat}
                picked={filter.tactics}
                onToggle={(tactic) => setFilter({ ...filter, tactics: toggleTactic(filter.tactics, tactic, options.tactics) })}
              />
            </div>

            {/* filter row */}
            <div className="hud-row sg-filters">
              <PickList
                label="Severity"
                options={[...SEVERITY_ORDER]}
                selected={filter.severities}
                onChange={(v) => setFilter({ ...filter, severities: v as SignalSeverity[] })}
                multi
                placeholder="severity…"
              />
              <PickList label="Tactic" options={options.tactics} selected={filter.tactics} onChange={(v) => setFilter({ ...filter, tactics: v })} multi placeholder="tactic…" />
              {windowLens ? (
                <PickList label="Detection" options={options.detections} selected={filter.detections} onChange={(v) => setFilter({ ...filter, detections: v })} multi placeholder="detection…" />
              ) : null}
              <PickList
                label="Stream"
                options={options.streams}
                selected={filter.streams}
                onChange={(v) => setFilter({ ...filter, streams: v })}
                multi
                placeholder="stream…"
                optionColor={(o) => (silentSet.has(o) ? 'var(--bui-error)' : undefined)}
              />
              {silent.length ? (
                <button
                  type="button"
                  className="sg-silent"
                  title={`No events so far today: ${silent.join(', ')}`}
                  onClick={() => setFilter({ ...filter, streams: silent })}
                >
                  ● {silent.length} silent
                </button>
              ) : null}
              {unsentStreams.length ? (
                <span className="q-facet-note" role="status">
                  not filtered on the server: {unsentStreams.join(', ')} — a stream name cannot hold these characters (CB-67)
                </span>
              ) : null}
              <PickList label="Stage" options={options.stages} selected={stages} onChange={setStages} multi placeholder="stage…" />
              <PickList
                label="Tag"
                options={tagOptions.map((o) => o.label)}
                selected={filter.tags.map((id) => tagLabelById.get(id) ?? id)}
                onChange={(v) => setFilter({ ...filter, tags: v.map((l) => tagIdByLabel.get(l)).filter((id): id is string => !!id) })}
                multi
                placeholder="tag…"
                optionColor={(l) => tagOptions.find((o) => o.label === l)?.color || undefined}
              />
              {signalTagsQ.isError ? (
                <span className="q-facet-note" role="status">
                  tags unavailable
                </span>
              ) : null}
              <input
                ref={entityInputRef}
                className="bui-input sg-entity-input sg-entity-inline"
                aria-label="Entity"
                placeholder="entity contains… (/)"
                value={filter.entity}
                onChange={(e) => setFilter({ ...filter, entity: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') (e.target as HTMLInputElement).blur()
                }}
              />
            </div>

            {/* lens row */}
            <div className="hud-row sg-filters">
              <div className="sg-pills" role="group" aria-label="Lens">
                {LENSES.map((l) => (
                  <button
                    key={l}
                    type="button"
                    className="list-chip"
                    aria-pressed={lens === l}
                    onClick={() => {
                      setFocus(-1)
                      setChecked(new Set())
                      // Storylines offers no False positives chip: leaving it there goes back to Unassigned.
                      setSearch({ lens: l, view: undefined, sg: undefined, stab: undefined, ...(l === 'storylines' && search.queue === 'fp' ? { queue: undefined } : {}) })
                    }}
                  >
                    {LENS_LABEL[l]}
                  </button>
                ))}
              </div>
              {lens === 'storylines' ? (
                <>
                  <div className="sg-pills" role="group" aria-label="Storylines view">
                    {(['list', 'graph'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        className="list-chip"
                        aria-pressed={storyView === v}
                        onClick={() => {
                          setFocus(-1)
                          setSearch({ view: v === 'graph' ? 'graph' : undefined, sg: undefined })
                        }}
                      >
                        {v === 'list' ? 'List' : 'Graph'}
                      </button>
                    ))}
                  </div>
                  <span className="sg-promo" role="group" aria-label="Storyline promotion">
                    promote at: score ≥
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="bui-input"
                      aria-label="Promotion score threshold"
                      value={settings.promotion.scoreThreshold}
                      onChange={(e) => {
                        const v = Number(e.target.value)
                        if (Number.isFinite(v) && v >= 0) settingsStore.set({ promotion: { ...settings.promotion, scoreThreshold: v } })
                      }}
                    />
                    or tactics ≥
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="bui-input"
                      aria-label="Promotion tactic threshold"
                      value={settings.promotion.tacticThreshold}
                      onChange={(e) => {
                        const v = Number(e.target.value)
                        if (Number.isFinite(v) && v >= 0) settingsStore.set({ promotion: { ...settings.promotion, tacticThreshold: v } })
                      }}
                    />
                  </span>
                </>
              ) : null}
              <span className="sg-filters-end">
                {lens === 'flat' ? (
                  <div className="sg-pills" role="group" aria-label="Sort this page">
                    {(['time', 'severity'] as const).map((k) => (
                      <button key={k} type="button" className="list-chip" aria-pressed={sortKey === k} onClick={() => setSortKey(k)}>
                        {k === 'time' ? 'newest' : 'severity'}
                      </button>
                    ))}
                  </div>
                ) : null}
                {lens === 'flat' ? (
                  <PickList
                    label="Columns"
                    options={TABLE_COLUMNS.map((c) => TABLE_COLUMN_LABEL[c])}
                    selected={columns.map((c) => TABLE_COLUMN_LABEL[c])}
                    onChange={(v) =>
                      viewStore.set({ columns: v.map((l) => COLUMN_BY_LABEL.get(l)).filter((c): c is TableColumn => !!c) })
                    }
                    multi
                    placeholder="columns…"
                  />
                ) : lens === 'storylines' && storyView === 'graph' ? null : (
                  <PickList
                    label="Member fields"
                    options={MEMBER_FIELDS.map((f) => MEMBER_FIELD_LABEL[f])}
                    selected={view.memberFields.map((f) => MEMBER_FIELD_LABEL[f])}
                    onChange={(v) =>
                      viewStore.set({ memberFields: v.map((l) => FIELD_BY_LABEL.get(l)).filter((f): f is MemberField => !!f) })
                    }
                    multi
                    placeholder="fields…"
                  />
                )}
                <div className="sg-pills" role="group" aria-label="Density">
                  {(['comfortable', 'compact'] as const).map((d) => (
                    <button key={d} type="button" className="list-chip" aria-pressed={view.density === d} onClick={() => viewStore.set({ density: d })}>
                      {d}
                    </button>
                  ))}
                </div>
                <button type="button" className="bui-btn bui-btn-ghost bui-btn-sm" title="Keyboard shortcuts (?)" onClick={() => setCheatsheet(true)}>
                  <kbd className="q-kbd">?</kbd>
                </button>
              </span>
            </div>

            {windowLens && signalsQ.truncated ? (
              <p className="q-facet-note" role="status">
                Showing the newest 2000 signals — narrow the window to see everything.
              </p>
            ) : null}

            {/* bulk bar */}
            {/* Counted from the ticked rows still loaded: a ticked uuid that left the page acts on nothing. */}
            {lens === 'flat' && checkedSignals.length ? (
              <div className="sg-bulkbar hud-row" role="region" aria-label="Bulk actions">
                <span className="hud-eyebrow">
                  {checkedSignals.length} selected{checkedSignals.length >= BULK_CAP ? ` (cap ${BULK_CAP})` : ''}
                </span>
                <button
                  type="button"
                  className="bui-btn bui-btn-outline bui-btn-sm"
                  disabled={flagFp.isPending}
                  onClick={() => toggleFp(checkedSignals, queue !== 'fp', () => setChecked(new Set()))}
                >
                  {queue === 'fp' ? 'Unmark false positive' : 'Mark false positive'}
                </button>
                <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" onClick={() => setEscalateReq({ signals: checkedSignals })}>
                  Add to case…
                </button>
                <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" onClick={() => requestClose(checkedSignals)}>
                  Close…
                </button>
                {dispositions ? (
                  <button
                    type="button"
                    className="bui-btn bui-btn-outline bui-btn-sm"
                    onClick={() =>
                      setApplyReq(
                        checkedSignals.map((s) => ({
                          signal: s,
                          suggestion: suggestTriage({ signal: s, dispositions, subgraphs: ranked, related: 0 }),
                        })),
                      )
                    }
                  >
                    Apply suggestions…
                  </button>
                ) : null}
                <button type="button" className="bui-btn bui-btn-ghost bui-btn-sm" onClick={() => setChecked(new Set())}>
                  Deselect
                </button>
              </div>
            ) : null}

            {/* the split: list | docked pane */}
            <div className="sg-split">
              <div className="sg-split-list" data-density={view.density}>
                {listTop}
                {lens === 'flat' ? (
                  pageQ.isLoading ? (
                    <p className="hud-unit" role="status">Loading signals…</p>
                  ) : pageQ.isError ? (
                    <div className="bui-alert bui-alert-error" role="alert">
                      <span className="bui-alert-mark">✕</span>
                      <span>
                        <span className="bui-alert-title">Signals failed to load</span>
                        {pageQ.error instanceof Error ? pageQ.error.message : 'unknown error'}
                      </span>
                      <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" onClick={() => void pageQ.refetch()}>
                        Retry
                      </button>
                    </div>
                  ) : pageQ.count === 0 ? (
                    <p className="hud-unit" role="status">
                      {flatFiltered ? 'No signals match the filters.' : emptyLine}
                    </p>
                  ) : (
                    <>
                      <div className="dense-table-wrap">
                        <table className="dense-table sg-flat" aria-label="Signals">
                          <thead>
                            <tr>
                              <th className="sg-th-check">
                                <input
                                  type="checkbox"
                                  aria-label="Select visible signals"
                                  checked={pageTick === 'all'}
                                  // Some ticked: the box shows partial, and a click ticks the rest.
                                  ref={(el) => {
                                    if (el) el.indeterminate = pageTick === 'some'
                                  }}
                                  onChange={() => setChecked(pageTick === 'all' ? new Set() : new Set(pageVisible.map((s) => s.uuid)))}
                                />
                              </th>
                              {has('time') ? <th>{TABLE_COLUMN_LABEL.time}</th> : null}
                              <th>detection</th>
                              {TABLE_COLUMNS.filter((c) => c !== 'time' && has(c)).map((c) => (
                                <th key={c} className={c === 'occurrences' ? 'num' : undefined}>
                                  {c === 'occurrences' ? 'occ.' : TABLE_COLUMN_LABEL[c]}
                                </th>
                              ))}
                              <th>
                                <span className="bui-sr-only">actions</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedFlat.map((s) => {
                              const key = `flat:${s.uuid}`
                              return (
                                <tr
                                  key={s.uuid}
                                  ref={rowRefFor(key)}
                                  data-severity={s.severity}
                                  data-focused={focusedKey === key || undefined}
                                  data-open={openUuid === s.uuid || undefined}
                                  title={`${s.severity} severity`}
                                  onClick={() => {
                                    setFocus(rows.findIndex((r) => r.key === key))
                                    openSignal(s.uuid, null)
                                  }}
                                >
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      aria-label={`Select ${s.detectionName}`}
                                      checked={checked.has(s.uuid)}
                                      disabled={!checked.has(s.uuid) && checked.size >= BULK_CAP}
                                      onChange={() => toggleChecked(s.uuid)}
                                    />
                                  </td>
                                  {has('time') ? <td>{cell('time', s)}</td> : null}
                                  <td>
                                    <span className="bui-sr-only">{s.severity} severity: </span>
                                    {s.detectionName || '—'}
                                    {s.fp ? <span className="sg-badge">FP</span> : null}
                                  </td>
                                  {TABLE_COLUMNS.filter((c) => c !== 'time' && has(c)).map((c) => (
                                    <td key={c} className={c === 'occurrences' ? 'num' : undefined}>
                                      {cell(c, s)}
                                    </td>
                                  ))}
                                  <td className="sg-td-actions" onClick={(e) => e.stopPropagation()}>
                                    {rowActions(s)}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                      {pager('bottom')}
                    </>
                  )
                ) : windowLens && signalsQ.isLoading ? (
                  <p className="hud-unit" role="status">Loading signals…</p>
                ) : windowLens && signalsQ.isError ? (
                  <div className="bui-alert bui-alert-error" role="alert">
                    <span className="bui-alert-mark">✕</span>
                    <span>
                      <span className="bui-alert-title">Signals failed to load</span>
                      {signalsQ.error instanceof Error ? signalsQ.error.message : 'unknown error'}
                    </span>
                  </div>
                ) : lens === 'storylines' ? (
                  connected.isLoading ? (
                    <p className="hud-unit" role="status">Building storylines…</p>
                  ) : connected.isError ? (
                    <div className="bui-alert bui-alert-error" role="alert">
                      <span className="bui-alert-mark">✕</span>
                      <span>
                        <span className="bui-alert-title">Storylines failed to build</span>
                        {connected.error instanceof Error ? connected.error.message : 'unknown error'}
                      </span>
                      <button type="button" className="bui-btn bui-btn-outline bui-btn-sm" onClick={() => void connected.refetch()}>
                        Retry
                      </button>
                    </div>
                  ) : ranked.length === 0 ? (
                    <p className="hud-unit" role="status">No connected signals in the last {window}.</p>
                  ) : (
                    <>
                      {connected.total > SUBGRAPH_CAP ? (
                        <p className="q-facet-note" role="status">
                          Showing {SUBGRAPH_CAP} of {connected.total} storylines.
                        </p>
                      ) : null}
                      {storyView === 'graph' ? (
                        <StorylineGraph
                          storylines={storylines}
                          focusKey={focusKey}
                          openUuid={openUuid}
                          onFocus={(k) => {
                            setPaneDismissed(false)
                            setSearch({ sg: k ?? undefined, signal: undefined })
                          }}
                          onOpenSignal={(u, k) => openSignal(u, k)}
                        />
                      ) : (
                        <div className="sg-list" role="list" aria-label="Storylines">
                          {pagedStorylines.map((st) => {
                            const key = `story:${st.key}`
                            const counts = { open: 0, in_progress: 0, closed: 0 }
                            for (const u of st.uuids) counts[triage.get(u)?.status ?? 'open']++
                            const isOpen = expanded.has(st.key)
                            const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`
                            const parts = [
                              plural(st.counts.signals, 'signal'),
                              st.counts.suspects ? plural(st.counts.suspects, 'suspect') : null,
                              st.counts.targets ? plural(st.counts.targets, 'target') : null,
                              st.counts.compromised ? `${st.counts.compromised} compromised` : null,
                            ].filter(Boolean)
                            return (
                              <div key={st.key} role="listitem" className="sg-story-wrap">
                                <div className="sg-story-row">
                                  {/* Members expand inline from the chevron; the row itself selects the storyline for the pane. */}
                                  <button
                                    type="button"
                                    className="qbc-x sg-story-expand"
                                    aria-expanded={isOpen}
                                    aria-label={`${isOpen ? 'Collapse' : 'Expand'} members of ${st.title}`}
                                    onClick={() => toggleExpanded(st.key)}
                                  >
                                    <Icon icon={isOpen ? ChevronDown : ChevronRight} size={13} />
                                  </button>
                                  <div
                                    ref={rowRefFor(key)}
                                    className="sg-story"
                                    data-severity={st.severity}
                                    data-focused={focusedKey === key || undefined}
                                    data-expanded={isOpen || undefined}
                                    data-open={paneStory?.key === st.key || undefined}
                                    role="button"
                                    tabIndex={-1}
                                    aria-current={paneStory?.key === st.key ? 'true' : undefined}
                                    onClick={() => {
                                      setFocus(rows.findIndex((r) => r.key === key))
                                      selectStory(st)
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        toggleExpanded(st.key)
                                      }
                                    }}
                                  >
                                    <span className="sg-story-main">
                                      <span className="sg-story-title">
                                        {st.watched ? (
                                          <span className="sg-watch-mark" title="watched" aria-label="watched">
                                            <Icon icon={Star} size={11} />
                                          </span>
                                        ) : null}
                                        <span>{st.title}</span>
                                        {st.promoted ? <span className="sg-badge">storyline</span> : null}
                                      </span>
                                      <span className="sg-story-sub muted">{parts.join(' · ')}</span>
                                    </span>
                                    <SeverityTag severity={st.severity} />
                                    <span className="num" title="storyline score">{st.score}</span>
                                    <Ago at={st.last} />
                                    <span className="sg-story-status muted">
                                      {counts.open} open
                                      {counts.in_progress ? ` · ${counts.in_progress} in progress` : ''}
                                      {counts.closed ? ` · ${counts.closed} closed` : ''}
                                    </span>
                                  </div>
                                  {/* Beside the row, not in it: the row is itself a button. */}
                                  <button
                                    type="button"
                                    className="bui-btn bui-btn-ghost bui-btn-sm sg-story-graph"
                                    aria-label={`Graph of storyline ${st.title}`}
                                    title="Show this storyline's graph"
                                    onClick={() => {
                                      setFocus(-1)
                                      setSearch({ view: 'graph', sg: st.key, signal: undefined })
                                    }}
                                  >
                                    <Icon icon={Network} size={13} />
                                  </button>
                                </div>
                                {isOpen ? storyMembers(st, key) : null}
                              </div>
                            )
                          })}
                        </div>
                      )}
                      {storyView === 'list' && groupLast > 1 ? groupPager('bottom') : null}
                    </>
                  )
                ) : filtered.length === 0 ? (
                  <p className="hud-unit" role="status">
                    {/* False positives filters the window rows here, so an empty queue has rows but no match. */}
                    {base.length === 0 || (queue === 'fp' && !flatFiltered) ? emptyLine : 'No signals match the filters.'}
                  </p>
                ) : (
                  <div className="sg-list" role="list" aria-label={lens === 'detection' ? 'By detection' : 'By tactic'}>
                    {pagedGroups.map((g) => {
                      const key = `group:${g.key}`
                      const isOpen = expanded.has(g.key)
                      const worst = g.members.reduce<SignalSeverity>(
                        (w, s) => (SEVERITY_ORDER.indexOf(s.severity) < SEVERITY_ORDER.indexOf(w) ? s.severity : w),
                        'info',
                      )
                      return (
                        <div key={g.key} role="listitem" className="sg-story-wrap">
                          <div
                            ref={rowRefFor(key)}
                            className="sg-story"
                            data-severity={worst}
                            data-focused={focusedKey === key || undefined}
                            data-expanded={isOpen || undefined}
                            role="button"
                            tabIndex={-1}
                            aria-expanded={isOpen}
                            onClick={() => {
                              setFocus(rows.findIndex((r) => r.key === key))
                              toggleExpanded(g.key)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                e.stopPropagation()
                                toggleExpanded(g.key)
                              }
                            }}
                          >
                            <span className="sg-story-main">
                              <span className="sg-story-title">{g.key}</span>
                              <span className="sg-story-sub muted">
                                {g.members.length} signal{g.members.length === 1 ? '' : 's'}
                              </span>
                            </span>
                            <SeverityTag severity={worst} />
                            <Ago at={g.members[0]!.at} />
                          </div>
                          {isOpen ? memberList(g.members.map((s) => s.uuid), key, null) : null}
                        </div>
                      )
                    })}
                  </div>
                )}
                {windowLens && groupHasRows && groupLast > 1 ? groupPager('bottom') : null}
              </div>

              <div className="sg-split-pane">
                {pane && !sheetOpen ? (
                  pane
                ) : storyPane && !sheetOpen ? (
                  storyPane
                ) : (
                  <aside className="sg-pane sg-pane-empty" aria-label="Signal">
                    <p className="q-facet-note">{sheetOpen ? 'Open in the full panel.' : 'Select a signal to read it here.'}</p>
                  </aside>
                )}
              </div>
            </div>

            <p className="sg-keys hud-unit" aria-hidden="true">
              j / k move · Enter open · e investigate · h hunt · x close · s suppress
              {dispositions ? ' · a accept AI suggestion' : ''} · / entity · ? keys
            </p>
          </div>
        </div>
      </div>

      {sheetOpen ? pane : null}

      {entityOpen && entityView ? (
        <EntityFlyout
          entity={entityOpen}
          history={entityView.history}
          prevalence={entityView.prevalence}
          cases={entityView.cases}
          suppressions={entityView.suppressions}
          watched={entityView.watched}
          open
          recent={entityView.recent}
          onClose={() => setEntityOpen(null)}
          onToggleWatch={() => settingsStore.toggleWatch(entityKey(entityOpen))}
          onOpenSearch={openSearch}
          onSelectSignal={(u) => {
            setEntityOpen(null)
            openSignal(u, null)
          }}
        />
      ) : null}

      <CloseDialog
        open={closeReq !== null}
        signals={closeReq?.signals ?? []}
        preset={closeReq?.preset ?? null}
        onCancel={() => setCloseReq(null)}
        onConfirm={onCloseConfirm}
      />

      <RemarkDialog
        open={remarkReq !== null}
        signal={remarkReq}
        busy={addRemark.isPending}
        error={addRemark.isError ? addRemark.error.message : null}
        onCancel={() => setRemarkReq(null)}
        onConfirm={onRemarkConfirm}
      />

      <ApplySuggestionsDialog open={applyReq !== null} items={applyReq ?? []} onCancel={() => setApplyReq(null)} onConfirm={onApplyConfirm} />

      <EscalateDialog
        open={escalateReq !== null}
        signals={escalateReq?.signals ?? []}
        prefill={escalateReq?.story?.prefill}
        cases={{
          items: pickQ.data?.items ?? [],
          total: pickQ.data?.total ?? 0,
          page: pickPage,
          pageSize: 25,
          loading: pickQ.isLoading,
          error: pickQ.isError ? pickQ.error.message : null,
          statuses: cmStatusesQ.data ?? [],
          status: pickStatus,
          onStatus: (k) => {
            setPickStatus(k)
            setPickPage(1)
          },
          category: pickCategory,
          onCategory: (c) => {
            setPickCategory(c)
            setPickPage(1)
          },
          onPage: setPickPage,
        }}
        users={cmUsersQ.data ?? []}
        pending={pending}
        busy={escalate.isPending}
        error={!pending && escalate.isError ? escalate.error.message : null}
        onCancel={() => {
          setEscalateReq(null)
          setPending(null)
          escalate.reset()
        }}
        onConfirm={onEscalateConfirm}
      />

      <SlideOver open={cheatsheet} onClose={() => setCheatsheet(false)} eyebrow="keyboard" title="Shortcuts">
        <dl className="sg-cheat">
          {(
            [
              ['j / k', 'move focus down / up — the pane follows'],
              ['v', 'storylines: switch List / Graph'],
              ['Enter', 'open the focused row (storyline: newest open member; graph: focus it)'],
              ['F', 'graph: focus the storyline of the focused node'],
              ['Esc', 'graph: clear the entity highlight, then back to all storylines'],
              ['e', 'toggle the deep panel of the open signal'],
              ['h', 'open as hunt'],
              ['x', 'close with disposition'],
              ['s', 'suppress (close as false positive — rule logic)'],
              ...(dispositions ? ([['a', 'accept the AI suggestion (opens Close prefilled)']] as const) : []),
              ['p or /', 'focus the entity filter'],
              ['?', 'this cheatsheet'],
            ] as const
          ).map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt>
                <kbd className="q-kbd">{k}</kbd>
              </dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </SlideOver>
    </div>
  )
}
